import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { baseUrl } from "api";
import { API_ENDPOINTS } from "api/apiEndPoints";

const PaymentSuccessPage: React.FC = () => {
  const { uuid } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<any>(null);

  useEffect(() => {
    const controller = new AbortController();
    async function fetchOrder() {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(
          `${baseUrl}${API_ENDPOINTS.SalesOrderByUuid}/${uuid}`,
          { signal: controller.signal }
        );
        const json = await res.json();
        if (!res.ok || !json?.success) {
          setError(json?.message || "Order not found");
        } else {
          const orderData = json.data;
          setOrder(orderData);
          
          // Send payment status to backend if it's failed or pending
          const paymentStatus = orderData?.payment_status?.toLowerCase() || "";
          if (
            paymentStatus === "failed" ||
            paymentStatus === "failure" ||
            paymentStatus === "pending" ||
            !paymentStatus ||
            paymentStatus === "" ||
            (paymentStatus !== "success" &&
              paymentStatus !== "paid" &&
              paymentStatus !== "completed" &&
              paymentStatus !== "complete")
          ) {
            // Determine the payment status to send
            let statusToSend = paymentStatus || "pending";
            if (
              paymentStatus === "failed" ||
              paymentStatus === "failure" ||
              paymentStatus === ""
            ) {
              statusToSend = "failed";
            }
            
            // Send full order data with updated payment_status to existing endpoint (PUT)
            try {
              const tokenStr = localStorage.getItem("token");
              const currentToken = tokenStr ? JSON.parse(tokenStr) : null;
              
              if (uuid && currentToken && orderData) {
                // Build full order payload with all data, just change payment_status
                const updatePayload = {
                  client: orderData?.client?.id || orderData?.client_id,
                  sale_order_item: orderData?.sales_order_item?.map((item: any) => ({
                    pid: item?.item_id || item?.pid,
                    item_name: item?.item_name || "",
                    qty: item?.qty || 0,
                    rate: item?.rate || 0,
                    total: item?.net_amount || item?.total || 0,
                    cgst: item?.cgst || 0,
                    cgst_per: item?.cgst_per || 0,
                    sgst: item?.sgst || 0,
                    sgst_per: item?.sgst_per || 0,
                    igst: item?.igst || 0,
                    igst_per: item?.igst_per || 0,
                    net_amount: item?.net_amount || 0,
                  })) || [],
                  user_address: orderData?.sales_order_address?.id || orderData?.user_address_id,
                  order_from: orderData?.order_from || "web",
                  sub_total: orderData?.sub_total || 0,
                  famount: orderData?.famount || orderData?.grand_total || 0,
                  g_total: orderData?.g_total || 0,
                  approved: orderData?.approved || "Pending",
                  approved_datetime: orderData?.approved_datetime || new Date().toISOString(),
                  sales_order_date: orderData?.sales_order_date || orderData?.order_date || new Date().toISOString().split('T')[0],
                  payment_status: statusToSend, // Change payment_status to failed/pending
                };
                
                // Update order with full data using existing endpoint
                await fetch(`${baseUrl}${API_ENDPOINTS.SalesOrder}/${uuid}`, {
                  method: "PUT",
                  headers: {
                    "Authorization": `Bearer ${currentToken}`,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify(updatePayload),
                });
              }
            } catch (statusError) {
              console.error("Failed to update payment status:", statusError);
              // Continue even if status update fails
            }
          }
        }
      } catch (e: any) {
        if (e.name !== "AbortError") setError("Unable to load order details. Please try again.");
      } finally {
        setLoading(false);
      }
    }
    if (uuid) fetchOrder();
    return () => controller.abort();
  }, [uuid]);

  if (loading) {
    return (
      <div className="container py-16">
        <h2 className="text-2xl font-semibold">Loading...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-16">
        <h2 className="text-2xl font-semibold text-red-600">Payment Success</h2>
        <p className="mt-4">{error}</p>
        <div className="mt-6 flex gap-3">
          <Link className="underline" to="/">Continue Shopping</Link>
        </div>
      </div>
    );
  }

  const taxAmount = Number(order?.g_total ?? 0);
  const subtotal = Number(order?.sub_total ?? 0);
  const total = Number(order?.famount ?? 0);

  // Determine payment status
  const paymentStatus = order?.payment_status?.toLowerCase() || "";
  const isPaymentSuccessful =
    paymentStatus === "success" ||
    paymentStatus === "paid" ||
    paymentStatus === "completed" ||
    paymentStatus === "complete";
  const isPaymentFailed =
    paymentStatus === "failed" ||
    paymentStatus === "failure" ||
    !paymentStatus ||
    paymentStatus === "" ||
    paymentStatus === "pending";

  return (
    <div className="container py-16">
      {isPaymentFailed ? (
        <>
          <h2 className="text-2xl font-semibold text-red-600">Payment Failed!</h2>
          <p className="mt-2 text-red-600">
            {paymentStatus === "pending"
              ? "Your payment is still pending. Please complete the payment to confirm your order."
              : "Your payment could not be processed. Please try again or contact support."}
          </p>
        </>
      ) : (
        <>
          <h2 className="text-2xl font-semibold text-green-600">Payment Successful!</h2>
          <p className="mt-2">Your order has been confirmed and is being processed.</p>
        </>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-semibold mb-3">Order Information</h3>
          <div className="space-y-1 text-sm">
            <div><strong>Order Number:</strong> {order?.sales_order_no}</div>
            <div><strong>Order Date:</strong> {order?.sales_order_date}</div>
            <div><strong>Payment Status:</strong> {order?.payment_status}</div>
            <div><strong>Order Status:</strong> {order?.approved}</div>
          </div>

          <h3 className="text-lg font-semibold mt-6 mb-3">Payment Details</h3>
          <div className="space-y-1 text-sm">
            <div><strong>Payment ID:</strong> {order?.razorpay_payment_id || order?.easepayid || "-"}</div>
            <div><strong>Transaction ID:</strong> {order?.txnid || "-"}</div>
            <div><strong>Payment Method:</strong> {order?.mode || "-"}</div>
          </div>

          <h3 className="text-lg font-semibold mt-6 mb-3">Customer</h3>
          <div className="space-y-1 text-sm">
            <div><strong>Name:</strong> {order?.customer?.name}</div>
            <div><strong>Email:</strong> {order?.customer?.email}</div>
            <div><strong>Mobile:</strong> {order?.customer?.mobile}</div>
          </div>

          <h3 className="text-lg font-semibold mt-6 mb-3">Shipping Address</h3>
          <div className="space-y-1 text-sm">
            {order?.sales_order_address ? (
              <>
                <div>{order?.sales_order_address?.address_line1}</div>
                <div>{order?.sales_order_address?.address_line2}</div>
                <div>{order?.sales_order_address?.area}</div>
                <div>{order?.sales_order_address?.state}</div>
                <div>{order?.sales_order_address?.city} {order?.sales_order_address?.pin_code}</div>
              </>
            ) : order?.customer?.user_addresses && order?.customer?.user_addresses.length > 0 ? (
              <>
                <div>{order?.customer?.user_addresses[0]?.address_line1}</div>
                <div>{order?.customer?.user_addresses[0]?.address_line2}</div>
                <div>{order?.customer?.user_addresses[0]?.area}</div>
                <div>{order?.customer?.user_addresses[0]?.state}</div>
                <div>{order?.customer?.user_addresses[0]?.city} {order?.customer?.user_addresses[0]?.pin_code}</div>
              </>
            ) : (
              <div className="text-slate-500 italic">No shipping address available</div>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-3">Items</h3>
          <div className="border rounded-lg divide-y">
            {order?.sales_order_item?.map((item: any) => {
              const tax = Number(item?.cgst || 0) + Number(item?.sgst || 0) + Number(item?.igst || 0);
              return (
                <div key={item?.id} className="p-4 text-sm flex justify-between">
                  <div>
                    <div className="font-medium">{item?.item_name || `Item #${item?.item_id}`}</div>
                    <div className="text-slate-500">Qty: {item?.qty}</div>
                    <div className="text-slate-500">Price: ₹{item?.rate}</div>
                    <div className="text-slate-500">Tax: ₹{tax}</div>
                  </div>
                  <div className="font-semibold">₹{item?.net_amount}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal}</span></div>
            <div className="flex justify-between"><span>Tax (GST)</span><span>₹{taxAmount}</span></div>
            <div className="border-t my-2" />
            <div className="flex justify-between font-semibold"><span>Total Amount Paid</span><span>₹{total}</span></div>
          </div>

          <div className="mt-8 flex gap-3">
            <Link className="px-4 py-2 rounded-2xl bg-slate-900 text-white" to="/account-my-order">View My Orders</Link>
            <Link className="px-4 py-2 rounded-2xl border" to="/">Continue Shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;


